import { useSearchAddress } from '@/searchAddress/useSearchAddress.ts';
import { LocationDetailsResponse } from '@/searchAddress/AddressTypes.ts';
import { useState } from 'react';
import { useForm } from '@/form/useForm.ts';
import { ConfigSlideId } from '@/eventConfig/types.ts';
import { FieldType, FieldValue } from '@/form/FormProvider.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { useFieldValidation, ValidationResult } from '@/form/fields/_pipeline/useValidateField.ts';

export const useUpdateAdressFields = () => {
  const { lookupAddress } = useSearchAddress();
  const { onChange, getFieldStatus, getFieldValue } = useForm();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { eventConfig } = useEventConfig();
  const s = eventConfig!.slides;
  const [locationDetailsResponse, setLocationsDetailsResponse] = useState<LocationDetailsResponse>();

  const fetchAddress = async (
    id: string,
    country?: string,
    language?: string,
    session_token?: string
  ): Promise<LocationDetailsResponse> => {
    return await lookupAddress({
      location_id: id,
      country: country,
      language: language,
      session_token: session_token,
    });
  };

  const createFieldValue = (value: FieldType) => {
    return { raw: value, normalized: value };
  };

  const pickText = (source: Record<string, unknown> | undefined, keys: string[]): string => {
    if (!source) return '';

    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  };

  const validate = (fieldName: string, value: FieldValue): ValidationResult => {
    const field = s.address.fields.find(({ name }) => name === fieldName);
    return validateField(field?.validator, value);
  };

  const updateField = (field: string, value: FieldType, baseVal: { slideId: ConfigSlideId }) => {
    const fieldValue = createFieldValue(value);

    onChange(field, {
      value: fieldValue,
      ...validate(field, fieldValue),
      ...baseVal,
    });
  };

  const updateAddressFields = async (id: string, country?: string, language?: string, session_token?: string) => {
    const address = await fetchAddress(id, country, language, session_token);
    setLocationsDetailsResponse(address);

    const baseVal = {
      slideId: 'address' as ConfigSlideId,
    };

    const normalizedAddress = (address as any)?.address;
    const providerAddress = (address as any)?.data;

    switch (country) {
      case 'DE': {
        updateField('street', address.address.street, baseVal);
        updateField('houseno', address.address.houseno, baseVal);
        updateField('zipcode', address.address.zipcode, baseVal);
        updateField('city', address.address.city, baseVal);
        break;
      }

      case 'CL': {
        updateField('street', address.address.street, baseVal);
        updateField('houseno', address.address.houseno, baseVal);
        updateField('zipcode', address.address.zipcode, baseVal);
        updateField('city', address.address.city, baseVal);
        break;
      }

      case 'GB': {
        updateField(
          'house',
          pickText(normalizedAddress ?? providerAddress, ['house', 'building_name', 'line_1']),
          baseVal
        );
        updateField('houseno', pickText(normalizedAddress ?? providerAddress, ['houseno', 'building_number']), baseVal);
        updateField('roadName', pickText(normalizedAddress ?? providerAddress, ['street', 'thoroughfare']), baseVal);
        updateField('town', pickText(normalizedAddress ?? providerAddress, ['city', 'post_town']), baseVal);
        updateField(
          'county',
          pickText(normalizedAddress ?? providerAddress, [
            'region',
            'postal_county',
            'administrative_county',
            'traditional_county',
            'county',
          ]),
          baseVal
        );
        updateField('postcode', pickText(normalizedAddress ?? providerAddress, ['zipcode', 'postcode']), baseVal);
        break;
      }

      case 'IN': {
        updateField('houseno', address.address.houseno, baseVal);
        updateField('roadName', address.address.street, baseVal);
        updateField('town', address.address.city, baseVal);
        updateField('pincode', address.address.zipcode, baseVal);
        updateField('state', address.address.state_code, baseVal);
        break;
      }

      case 'US': {
        updateField('houseno', address.address.houseno, baseVal);
        updateField('street', address.address.street, baseVal);
        updateField('city', address.address.city, baseVal);
        updateField('state', address.address.state_code, baseVal);
        updateField('zipcode', address.address.zipcode, baseVal);
        break;
      }

      case 'PL': {
        updateField('street', address.address.street, baseVal);
        updateField('houseno', address.address.houseno, baseVal);
        updateField('zipcode', address.address.zipcode, baseVal);
        updateField('city', address.address.city, baseVal);
        break;
      }

      case 'ZA': {
        updateField('street', address.address.street, baseVal);
        updateField('houseno', address.address.houseno, baseVal);
        updateField('city', address.address.city, baseVal);
        updateField('zipcode', address.address.zipcode, baseVal);
        break;
      }

      default:
        console.log('No Address fields are updated.');
        break;
    }
  };
  return { updateAddressFields, locationDetailsResponse };
};
